"use client"
import type { OklchColor } from "@/lib/color-utils"
import { ProgressProvider } from "@bprogress/next/app"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { ThemeProvider } from "next-themes"
import { usePathname } from "next/navigation"
import { NuqsAdapter } from "nuqs/adapters/next/app"
import { useEffect, useMemo, useState } from "react"
import { getQueryClient } from "@/app/[locale]/get-query-client"
import { CustomThemeProvider } from "@/components/provider/custom-theme-provider"
import { ReactScan } from "@/components/react-scan"
import { Toaster } from "@/components/ui/sonner"
import { ConfirmProvider } from "@/hooks/confirm-provider"
import { MainLayout } from "@/layouts/sidebar-layout"
import { oklchToHex, parseOklchCss } from "@/lib/color-utils"
import "@/services/post-message.service"

export default function Providers({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient()
  const [primaryColor, setPrimaryColor] = useState<OklchColor>({ l: 0.205, c: 0, h: 0 })

  useEffect(() => {
    const primaryColorCss = getComputedStyle ? getComputedStyle(document.documentElement).getPropertyValue("--primary") : "oklch(0.205 0 0)"
    setPrimaryColor(parseOklchCss(primaryColorCss))
  }, [])

  // Get the primary color from CSS variables and parse it
  const isInLogin = usePathname().includes("login")

  return (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
        >

          <CustomThemeProvider>
            <ProgressProvider
              color={oklchToHex(primaryColor)}
              height="4px"
              options={{ showSpinner: false }}
              shallowRouting
            >
              <ConfirmProvider>
                {!isInLogin
                  ? (
                      <MainLayout>
                        {children}
                      </MainLayout>
                    )
                  : (
                      <>{children}</>
                    )}
              </ConfirmProvider>
            </ProgressProvider>
          </CustomThemeProvider>
        </ThemeProvider>
      </NuqsAdapter>
      <Toaster richColors />
      {process.env.NODE_ENV === "development" && (
        <>
          <ReactQueryDevtools />
          <ReactScan />
        </>
      )}
    </QueryClientProvider>
  )
}
